import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useBookmarks } from "../../../../../hooks/useBookmarks";
import { usePagination } from "../../../../../hooks/usePagination";
import { NewsFilters } from "./NewsFilters";
import { NewsPagination } from "./NewsPagination";
import { NewsTableRow } from "./NewsTableRow";
import { fetchGeneralNews } from "../../../../../lib/api_routes/news";
import { COMPONENT_SPACING } from "../../../../../utils/constants/designConstants";
import { SectionFallback } from "../../../../../utils/error_message";
import { useAccountInfo } from "../../../../../hooks/useAccountInfo";

const TABLE_HEADERS = [
  "Bookmarks",
  "Section",
  "Title",
  "Description",
  "Published",
];

export const GeneralNewsTable = ({ generalNews, formValues }) => {
  const accountInfo = useAccountInfo();
  const accountId = accountInfo?.accountInfo?.accountId;
  const [fetchError, setFetchError] = useState(false);
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  const [bookmarkedNewsItems, setBookmarkedNewsItems] = useState([]);
  const [clickedLinks, setClickedLinks] = useState(new Set());

  const { bookmarksByAccount, bookmarksLoaded, toggleBookmark } =
    useBookmarks(accountId);

  const {
    newsCache,
    currentItems,
    setCurrentItems,
    currentPage,
    isLoading,
    canGoToNextPage,
    handlePageChange,
    handleNextPage,
    handlePrevPage,
    getPageNumbers,
    rowsPerPage,
  } = usePagination(generalNews, formValues, accountInfo, bookmarksLoaded);

  const currentBookmarks = bookmarksByAccount[accountId] || new Set();
  const realTimeBookmarksCount = currentBookmarks.size;

  // Fetch bookmarked news
  const fetchBookmarkedNews = async () => {
    try {
      const bookmarkedNewsResponse = await fetchGeneralNews({
        country: formValues?.country || "",
        offset: 0,
        limit: 100,
        bookmarks_only: true,
      });

      if (!bookmarkedNewsResponse) {
        setFetchError(true);
        return;
      }

      setFetchError(false);
      let bookmarkedNews = Array.isArray(bookmarkedNewsResponse)
        ? bookmarkedNewsResponse
        : (bookmarkedNewsResponse?.news_list ?? []);

      setBookmarkedNewsItems(bookmarkedNews);
    } catch (error) {
      console.error("Error fetching bookmarked news:", error);
      setFetchError(true);
      setBookmarkedNewsItems([]);

      setCurrentItems([]);
    }
  };

  // Load bookmarked news on mount
  useEffect(() => {
    if (accountId) {
      fetchBookmarkedNews();
    }
  }, [accountId]);

  if (!accountInfo) return <CircularProgress size={24} />;

  // Check error first, before the empty data check
  if (fetchError) {
    return (
      <SectionFallback message="Failed to load General News. Please try again later." />
    );
  }

  if (
    !generalNews ||
    (Array.isArray(generalNews) && generalNews.length === 0) ||
    (generalNews.news_list &&
      Array.isArray(generalNews.news_list) &&
      generalNews.news_list.length === 0)
  ) {
    return (
      <SectionFallback message="General News aren't available at the moment." />
    );
  }

  // Handle bookmarks filter toggle
  const handleBookmarksToggle = async (checked) => {
    setShowBookmarksOnly(checked);
    if (checked) {
      await fetchBookmarkedNews();
    } else {
      // Restore cached news when turning off filter
      if (newsCache.has(currentPage)) {
        setCurrentItems(newsCache.get(currentPage));
      }
    }
  };

  // Handle bookmark toggle with UI update
  const handleToggleBookmark = async (newsId) => {
    const newsIdStr = String(newsId);
    const isCurrentlyBookmarked = currentBookmarks.has(newsIdStr);

    if (showBookmarksOnly && isCurrentlyBookmarked) {
      setCurrentItems((prev) =>
        prev.filter((newsItem) => String(newsItem.news_id) !== newsIdStr),
      );
    }

    // Let the hook handle state updates
    await toggleBookmark(newsIdStr);
  };

  // Use bookmarksByAccount from hook directly instead of local state
  const getBookmarkStatus = (newsId) => {
    // Check against the hook's state with the full news_id
    return currentBookmarks.has(String(newsId));
  };

  const newsItemsToDisplay = showBookmarksOnly
    ? bookmarkedNewsItems
    : currentItems;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Filter Component */}
      <NewsFilters
        itemsCount={currentItems.length}
        totalCount={generalNews.count}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        bookmarksCount={realTimeBookmarksCount}
        showBookmarksOnly={showBookmarksOnly}
        onToggleBookmarks={handleBookmarksToggle}
      />

      {/* News Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: 0, backgroundColor: "transparent", boxShadow: "none" }}
      >
        <Table
          sx={{
            minWidth: COMPONENT_SPACING.MIN_TABLE_WIDTH,
            borderSpacing: "0 8px",
            borderCollapse: "separate",
          }}
          stickyHeader
          aria-label="general news"
          size="small"
        >
          {/* Table Headers */}
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    backgroundColor: "primary.white",
                    fontWeight: "bold",
                    py: 2,
                    border: 0,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading news...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : Array.isArray(newsItemsToDisplay) &&
              newsItemsToDisplay.length > 0 ? (
              newsItemsToDisplay.map((item, index) => (
                // NewsTableRow Component
                <NewsTableRow
                  key={`${currentPage}-${index}`}
                  item={item}
                  index={index}
                  currentPage={currentPage}
                  isBookmarked={getBookmarkStatus}
                  toggleBookmark={toggleBookmark}
                  clickedLinks={clickedLinks}
                  setClickedLinks={setClickedLinks}
                  onToggleBookmark={handleToggleBookmark}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {showBookmarksOnly
                      ? "No bookmarked articles found"
                      : "No news data available"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Component */}
      {!showBookmarksOnly && (
        <NewsPagination
          currentPage={currentPage}
          isLoading={isLoading}
          canGoToNextPage={canGoToNextPage()}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
          onPageChange={handlePageChange}
          getPageNumbers={getPageNumbers}
        />
      )}
    </Box>
  );
};
